// L'ETAPA 5 DEL SSM: LA COMPARACIÓ.
//
// Fora de React i sense cap import. Es prova sola: `npm run prova:comparacio`.
//
// ── QUÈ ÉS, I PER QUÈ ÉS L'ETAPA QUE FA SERVIR LA 4 ──────────────────────────────────────────
//
// El model conceptual **no descriu el món**: descriu què hauria de passar si el sistema fos com
// diu una definició arrel. L'etapa 5 el posa **al costat del que passa de debò**, activitat per
// activitat, i el que en surt no és una nota: és **la llista de coses de què val la pena parlar**.
//
// Checkland dona quatre maneres de comparar. Aquí hi ha la segona —**l'interrogatori formal**,
// una taula amb una fila per activitat—, que és la que es fa servir a l'aula perquè és la que
// deixa rastre i la que es pot discutir en grup. Les altres tres (conversa informal, escriure
// escenaris fent «córrer» el model, i modelitzar el món real amb la mateixa estructura) no hi
// són, i **no hi són enunciades**: una pantalla que anomena el que no fa és el defecte que
// aquesta aplicació ja arrossegava.
//
// ── LES COLUMNES ─────────────────────────────────────────────────────────────────────────────
//
//     l'activitat        del model conceptual, no del món
//     passa de debò?     sí / a mitges / no
//     com es fa?         si passa, de quina manera
//     com es jutja?      quina mesura de rendiment se li aplica ARA, si n'hi ha cap
//     comentaris         què se n'ha dit, i què es podria fer diferent
//
// La quarta és la que més s'oblida i la que més val: **una activitat que passa i que ningú no
// jutja** és tan interessant com una que no passa.

export const RESPOSTES = ['yes', 'partly', 'no'];

// Les files surten de les activitats operatives del model conceptual, **aparellades per `id`**.
//
// Mai pel número: els números d'un model conceptual es calculen de la posició i esborrar una
// activitat del mig les renumera totes. Una taula lligada al número quedaria desalineada **en
// silenci**, que és la mena d'errada que ningú no veu fins que ja ha discutit mitja classe sobre
// la fila equivocada.
export function fileraDe(model, respostes = {}) {
  const ops = (model?.activities || []).filter((a) => a.type !== 'monitoring');
  return ops.map((a, i) => ({
    activityId: a.id,
    n: i + 1,
    name: a.name,
    ...(respostes[a.id] || { exists: '', how: '', judged: '', comments: '' }),
  }));
}

// Les respostes que han quedat penjades d'una activitat que ja no hi és.
//
// Passa de debò: es compara, després es torna a l'etapa 4 i es reordena el model. Deixar-les
// desades i no ensenyar-les seria amagar feina; esborrar-les soles seria perdre-la. Es diuen, i
// qui compara decideix.
export function respostesOrfes(model, respostes = {}) {
  const vius = new Set((model?.activities || []).map((a) => a.id));
  return Object.keys(respostes).filter((id) => !vius.has(id));
}

export const netejaOrfes = (model, respostes = {}) => {
  const orfes = new Set(respostesOrfes(model, respostes));
  return Object.fromEntries(Object.entries(respostes).filter(([id]) => !orfes.has(id)));
};

const buida = (v) => !String(v || '').trim();

// Quantes files s'han contestat. Una fila compta si s'ha dit si l'activitat passa o no: la resta
// de columnes són per explicar-ho, i moltes files bones es queden amb un «no» i prou.
export const contestades = (files) => files.filter((f) => RESPOSTES.includes(f.exists)).length;

// ── EL QUE ALIMENTA L'ETAPA 6 ────────────────────────────────────────────────────────────────
//
// Aquí és on la taula deixa de ser un exercici d'omplir caselles. **Cada diferència és un
// candidat a canvi**, i el motiu pel qual ho és queda escrit: a l'etapa 6 s'hi decidirà si és
// desitjable i si és factible, però la llista no se l'ha d'inventar ningú.
export function diferencies(files) {
  const fora = [];
  for (const f of files) {
    if (f.exists === 'no') {
      fora.push({ activityId: f.activityId, name: f.name, clau: 'ssm.cpDifNoPassa' });
    } else if (f.exists === 'partly') {
      fora.push({ activityId: f.activityId, name: f.name, clau: 'ssm.cpDifAMitges' });
    } else if (f.exists === 'yes' && buida(f.judged)) {
      // Una activitat que passa i que ningú no jutja: el sistema la fa i no sap si la fa bé.
      fora.push({ activityId: f.activityId, name: f.name, clau: 'ssm.cpDifSenseMesura' });
    }
  }
  return fora;
}

// El resum d'una comparació. El fa servir la prova; la capçalera de l'etapa fa servir
// `contestades` directament, que és el número que hi interessa.
export function resum(model, respostes = {}) {
  const files = fileraDe(model, respostes);
  return {
    files: files.length,
    contestades: contestades(files),
    diferencies: diferencies(files).length,
    orfes: respostesOrfes(model, respostes).length,
  };
}

const COMPARACIO = { RESPOSTES, fileraDe, respostesOrfes, netejaOrfes, contestades,
                     diferencies, resum };
export default COMPARACIO;
